// Steps 7 and 8: send, then thank you.
// Configured: dynamic-import the Firebase code, upload with real progress, call submitRecording.
// Any failure: Try again, Download my recordings, Email them to us, Copy debug log.
// Not configured (OH_CONFIG.firebase === null): download each recording and email it to us.
import { isBackendConfigured } from '../lib/config.js';
import { logger } from '../lib/log.js';
import { announce } from '../lib/forms.js';
import { friendlyError, errorField, isExpectedRefusal } from '../lib/errors.js';
import { $, el, show, fmtLength } from './dom.js';
import { downloadName, recordingsMailto } from './fallback.js';
import { keepAwake } from './wakelock.js';

const log = logger('record');

export function createSendScreen(app) {
  const { root, state, data, cfg } = app;
  const sec = $('[data-step="send"]', root);
  const q = (s) => $(s, sec);
  const ui = {
    h: q('[data-send-h]'),
    progress: q('[data-send-progress]'), status: q('[data-send-status]'), bar: q('[data-send-bar]'),
    offline: q('[data-send-offline]'), offlineH: q('[data-send-offline-h]'),
    failed: q('[data-send-failed]'), failedH: q('[data-send-failed-h]'), error: q('[data-send-error]'),
    manualIntro: q('[data-send-manual-intro]'), fallback: q('[data-fallback]'), downloads: q('[data-download-list]'),
    mailto: q('[data-mailto="send"]'), debug: q('[data-fallback-debug]'),
    manualDone: q('[data-send-manual-done]'), manualConfirm: q('[data-manual-confirm]'), manualConfirmH: q('[data-manual-confirm-h]'),
    back: q('[data-send-back]'),
  };
  ui.slow = q('[data-send-slow]');
  const thanks = $('[data-step="thanks"]', root);
  const STALL_MS = 45000;
  let lastStatus = '';
  let lastPct = -1;
  let lastMoveAt = 0;
  let stallTimer = null;

  /** No progress for a while (a weak connection): say so, and offer the download/email fallback. */
  function watchStall(on) {
    clearInterval(stallTimer);
    stallTimer = null;
    show(ui.slow, false);
    if (!on) return;
    lastMoveAt = Date.now();
    stallTimer = setInterval(() => {
      if (!state.sending || !ui.slow.hidden || Date.now() - lastMoveAt < STALL_MS) return;
      show(ui.slow, true);
      renderFallback('send_slow');
      announce('This is taking longer than usual. Please keep this page open.');
      log.warn('send_slow', { submissionId: state.draft.submissionId, pct: lastPct });
    }, 5000);
  }

  function setProgress(fraction) {
    const pct = Math.round(Math.max(0, Math.min(1, fraction)) * 100);
    if (pct !== lastPct) { lastPct = pct; lastMoveAt = Date.now(); }
    ui.bar.setAttribute('aria-valuenow', String(pct));
    ui.bar.setAttribute('aria-valuetext', `${pct} percent sent`);
    ui.bar.firstElementChild.style.width = `${pct}%`;
  }
  function setStatus(text, speak = true) {
    lastMoveAt = Date.now();
    ui.status.textContent = text;
    if (speak && text !== lastStatus) announce(text);
    lastStatus = text;
  }

  function hideAll() {
    [ui.progress, ui.slow, ui.offline, ui.failed, ui.manualIntro, ui.fallback, ui.manualDone, ui.manualConfirm, ui.back, ui.debug].forEach((x) => show(x, false));
  }

  /** Download links (one per answer: each is a real tap, which phones need) and the mailto. */
  function renderFallback(where) {
    ui.downloads.replaceChildren(...state.clips.map((c, i) => {
      const p = data.promptById(c.promptId);
      const name = downloadName(i + 1, c.mimeType);
      const a = el('a', { class: 'btn btn-ghost rec-download', href: c.url, download: name, 'data-download': String(i + 1) }, [
        el('span', { class: 'rec-download__icon', 'aria-hidden': 'true' }), `Download answer ${i + 1}`,
      ]);
      a.addEventListener('click', () => log.info('fallback_download', { where, index: i + 1, count: state.clips.length, mimeType: c.mimeType }));
      return el('li', {}, [a, el('span', { class: 'rec-download__meta' }, `${p ? p.text : ''} (${fmtLength(c.durationSec)})`)]);
    }));
    ui.mailto.href = recordingsMailto({ to: cfg.contactEmail, clips: state.clips, data, cfg, form: state.form });
    ui.mailto.setAttribute('data-where', where);
    show(ui.fallback, true);
  }
  ui.mailto.addEventListener('click', () => log.info('fallback_email', { where: ui.mailto.getAttribute('data-where') || 'send', count: state.clips.length }));

  function showManual() {
    hideAll();
    ui.h.textContent = 'Send your memories by email';
    show(ui.manualIntro, true);
    renderFallback('not_configured');
    show(ui.manualDone, true);
    show(ui.back, true);
  }

  function showFailure(err) {
    hideAll();
    ui.h.textContent = 'Sending your memories';
    ui.error.textContent = friendlyError(err);
    // Most messages already say the recordings are safe; do not say it twice.
    show(q('[data-send-safe]'), !/(safe|saved) on this device/i.test(ui.error.textContent));
    show(ui.failed, true);
    renderFallback('send_failed');
    show(ui.debug, true);
    show(ui.back, true);
    app.focusHeading(ui.failedH, false);
    announce(`We could not send your memories yet. ${ui.error.textContent}`);
  }

  async function startSend() {
    if (state.sending) return;
    if (!state.clips.length) { app.go('review'); return; }
    if (!state.form) { app.go('about'); return; }
    if (!isBackendConfigured()) { showManual(); return; }
    hideAll();
    ui.h.textContent = 'Sending your memories';
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      log.warn('submit_offline', { submissionId: state.draft.submissionId });
      show(ui.offline, true);
      renderFallback('offline');
      show(ui.back, true);
      app.focusHeading(ui.offlineH, false);
      announce('You are not connected to the internet. Your recordings are safe on this device.');
      return;
    }

    state.sending = true;
    keepAwake(true);
    lastPct = -1;
    watchStall(true);
    show(ui.progress, true);
    setProgress(0);
    setStatus('Getting ready…', false);
    const clips = state.clips.slice();
    log.info('submit_start', { submissionId: state.draft.submissionId, clipCount: clips.length, hasPhoto: !!state.photo });
    try {
      const { sendSubmission } = await import('./submit.js');
      const f = state.form;
      const result = await sendSubmission({
        draft: state.draft,
        clips,
        photo: state.photo,
        maxClips: app.maxClips(),
        maxSeconds: app.maxSeconds(),
        payloadBase: {
          contributor: {
            name: f.name, email: f.email || null, phone: f.phone || null,
            connection: f.connection, connectionOther: f.connectionOther || null,
            yearsInvolved: f.yearsInvolved, credit: f.credit,
          },
          visibilityChoice: f.visibilityChoice,
          consent: { version: cfg.consentVersion, accepted: true, acceptedAt: state.consentAt || new Date().toISOString() },
        },
        promptIdFor: app.promptIdFor,
        saveDraft: app.saveDraft,
        saveClip: app.saveClip,
        rotate: app.rotateSubmission,
        onStage: (stage, info) => {
          if (stage === 'auth') setStatus('Connecting…', false);
          else if (stage === 'upload') setStatus(info.kind === 'photo' ? 'Sending your photo' : `Sending answer ${info.index} of ${info.count}`);
          else if (stage === 'submit') { setProgress(1); setStatus('Almost done. Saving your answers…'); }
        },
        onProgress: setProgress,
      });
      log.info('submit_ok', { submissionId: result.submissionId, referenceCode: result.referenceCode, clipCount: clips.length });
      state.lastResult = { referenceCode: result.referenceCode, hadEmail: !!f.email };
      state.sending = false;
      watchStall(false);
      keepAwake(false);
      await app.clearAll();
      app.go('thanks');
    } catch (err) {
      state.sending = false;
      watchStall(false);
      keepAwake(false);
      const field = errorField(err);
      // An expected refusal (ARCHITECTURE.md 11.7) is a warning, so a mistyped email never lands in
      // the admin "Recent errors" panel.
      log[isExpectedRefusal(err) ? 'warn' : 'error']('submit_failed', { submissionId: state.draft.submissionId, code: err && err.code, field, message: err && err.message });
      if (field && app.screens.about.showServerError(field, friendlyError(err))) return;
      showFailure(err);
    }
  }

  app.on('send-retry', () => startSend());
  app.on('manual-done', () => { show(ui.manualConfirm, true); app.focusHeading(ui.manualConfirmH, false); announce('Clear the recordings from this device? Only do this after your email has been sent.'); });
  app.on('manual-cancel', () => { show(ui.manualConfirm, false); const b = $('[data-action="manual-done"]', sec); if (b) b.focus(); });
  app.on('manual-confirm', async () => {
    log.info('manual_send_done', { count: state.clips.length });
    state.lastResult = { emailed: true };
    await app.clearAll();
    app.go('thanks');
  });
  app.on('record-another', async () => {
    log.info('record_another', {});
    if (state.clips.length || state.take) await app.clearAll();
    app.go('welcome');
  });

  return {
    send: {
      handlesFocus: true,
      onShow(opts = {}) {
        const focus = () => { if (opts.focus !== false) app.focusHeading(ui.h, `Part 4 of 4, Send. ${ui.h.textContent}`); };
        if (!isBackendConfigured()) { showManual(); focus(); return; }
        ui.h.textContent = 'Sending your memories';
        focus();
        if (opts.auto) startSend();
        else if (!state.sending) { hideAll(); show(ui.back, true); renderFallback('send'); }
      },
    },
    thanks: {
      onShow() {
        const r = state.lastResult || {};
        const ref = $('[data-thanks-ref]', thanks);
        show(ref, !!r.referenceCode);
        show($('[data-thanks-emailed]', thanks), !r.referenceCode);
        $('[data-refcode]', thanks).textContent = r.referenceCode || '';
        show($('[data-thanks-email]', thanks), !!r.hadEmail);
      },
    },
  };
}
